import { Capacitor } from '@capacitor/core'
import { apiRequest } from './base'

// ─── Response types ─────────────────────────────────────────

export interface CalendarInfo {
  id: string
  name: string
  timezone: string | null
  isPrimary: boolean
  isWritable: boolean
  syncEnabled: boolean
}

export interface ConnectionInfo {
  connectionId: string
  provider: string
  displayName?: string
  status: string
  scopes: string[]
  connectedAt: string
  calendarSyncEnabled?: boolean
  emailSyncEnabled?: boolean
  calendars?: CalendarInfo[]
}

interface ConnectionsResponse {
  connections: ConnectionInfo[]
}

interface ConnectResponse {
  authUrl: string
  state: string
}

interface DisconnectResponse {
  id: string
  status: string
}

// ─── Helpers ────────────────────────────────────────────────

/** Returns 'native' when running inside a Capacitor native shell, undefined otherwise. */
function getNativePlatform(): string | undefined {
  return Capacitor.isNativePlatform() ? 'native' : undefined
}

// ─── API functions ──────────────────────────────────────────

export async function getConnections(): Promise<ConnectionsResponse> {
  return apiRequest<ConnectionsResponse>('/settings/connections')
}

export async function connectGoogle(options?: { features?: string[] }): Promise<ConnectResponse> {
  return apiRequest<ConnectResponse>('/settings/connect/google', {
    method: 'POST',
    body: JSON.stringify({ features: options?.features, platform: getNativePlatform() }),
  })
}

/** Request Gmail-specific scopes (incremental consent on existing Google connection). */
export async function connectGoogleGmail(): Promise<ConnectResponse> {
  return apiRequest<ConnectResponse>('/settings/connect/google/gmail', {
    method: 'POST',
    body: JSON.stringify({ platform: getNativePlatform() }),
  })
}

export async function disconnectService(connectionId: string): Promise<DisconnectResponse> {
  return apiRequest<DisconnectResponse>(`/settings/connections/${connectionId}`, {
    method: 'DELETE',
  })
}

/** Toggle calendar sync on/off for a specific calendar. */
export async function toggleCalendarSync(calendarId: string, syncEnabled: boolean) {
  return apiRequest<{ id: string; syncEnabled: boolean }>(`/settings/calendars/${calendarId}`, {
    method: 'PATCH',
    body: JSON.stringify({ syncEnabled }),
  })
}

/** Toggle per-connection service sync (calendar/gmail). */
export async function toggleServiceSync(connectionId: string, services: { calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }) {
  return apiRequest<{ id: string; calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }>(`/settings/connections/${connectionId}/services`, {
    method: 'PATCH',
    body: JSON.stringify(services),
  })
}

/** Generic plugin connection — works for any plugin with OAuth2. */
export async function connectPlugin(pluginId: string, options?: { features?: string[] }): Promise<ConnectResponse> {
  return apiRequest<ConnectResponse>(`/settings/connect/${pluginId}`, {
    method: 'POST',
    body: JSON.stringify({ features: options?.features, platform: getNativePlatform() }),
  })
}
